<script setup>
import { computed, useTemplateRef } from 'vue';
import { useElementSize } from '@vueuse/core';
import { REPLY_EDITOR_MODES } from './constants';

const props = defineProps({
  mode: {
    type: String,
    default: REPLY_EDITOR_MODES.REPLY,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  isReplyRestricted: {
    type: Boolean,
    default: false,
  },
  // Muestra el tercer segmento "Traducción" (feature Traducción automática).
  showTranslate: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['setMode']);

const wootEditorReplyMode = useTemplateRef('wootEditorReplyMode');
const wootEditorPrivateMode = useTemplateRef('wootEditorPrivateMode');
const wootEditorTranslateMode = useTemplateRef('wootEditorTranslateMode');

const replyModeSize = useElementSize(wootEditorReplyMode);
const privateModeSize = useElementSize(wootEditorPrivateMode);
const translateModeSize = useElementSize(wootEditorTranslateMode);

/**
 * When isReplyRestricted is true, force private note mode.
 * @type {ComputedRef<string>}
 */
const activeMode = computed(() => {
  if (props.isReplyRestricted) {
    return REPLY_EDITOR_MODES.NOTE;
  }
  if (props.mode === REPLY_EDITOR_MODES.TRANSLATE && !props.showTranslate) {
    return REPLY_EDITOR_MODES.REPLY;
  }
  return props.mode;
});

/**
 * Width of the sliding background chip (16px of padding included).
 * @type {ComputedRef<string>}
 */
const width = computed(() => {
  let widthToUse = replyModeSize.width.value;
  if (activeMode.value === REPLY_EDITOR_MODES.NOTE) {
    widthToUse = privateModeSize.width.value;
  } else if (activeMode.value === REPLY_EDITOR_MODES.TRANSLATE) {
    widthToUse = translateModeSize.width.value;
  }

  const widthWithPadding = widthToUse + 16;
  return `${widthWithPadding}px`;
});

/**
 * X translation for the sliding chip: sum of the widths of the
 * segments before the active one (each with 16px padding).
 * @type {ComputedRef<string>}
 */
const translateValue = computed(() => {
  let xTranslate = 0;
  if (activeMode.value === REPLY_EDITOR_MODES.NOTE) {
    xTranslate = replyModeSize.width.value + 16;
  } else if (activeMode.value === REPLY_EDITOR_MODES.TRANSLATE) {
    xTranslate = replyModeSize.width.value + privateModeSize.width.value + 32;
  }

  return `${xTranslate}px`;
});

const selectMode = mode => {
  if (props.disabled) return;
  if (props.isReplyRestricted && mode !== REPLY_EDITOR_MODES.NOTE) return;
  emit('setMode', mode);
};
</script>

<template>
  <div
    data-zoho-tour="editor-mode-toggle"
    class="flex items-center w-auto h-8 p-1 transition-all border rounded-full bg-n-alpha-2 group relative duration-300 ease-in-out z-0"
    :class="{
      'cursor-not-allowed': disabled || isReplyRestricted,
    }"
  >
    <button
      ref="wootEditorReplyMode"
      type="button"
      class="flex items-center gap-1 px-2 z-20 h-full"
      :disabled="disabled || isReplyRestricted"
      :class="{
        'cursor-not-allowed': disabled || isReplyRestricted,
      }"
      @click="selectMode(REPLY_EDITOR_MODES.REPLY)"
    >
      {{ $t('CONVERSATION.REPLYBOX.REPLY') }}
    </button>
    <button
      ref="wootEditorPrivateMode"
      type="button"
      class="flex items-center gap-1 px-2 z-20 h-full"
      :disabled="disabled"
      :class="{ 'cursor-not-allowed': disabled }"
      @click="selectMode(REPLY_EDITOR_MODES.NOTE)"
    >
      {{ $t('CONVERSATION.REPLYBOX.PRIVATE_NOTE') }}
    </button>
    <button
      v-if="showTranslate"
      ref="wootEditorTranslateMode"
      type="button"
      class="flex items-center gap-1 px-2 z-20 h-full"
      :disabled="disabled || isReplyRestricted"
      :class="{
        'cursor-not-allowed': disabled || isReplyRestricted,
      }"
      @click="selectMode(REPLY_EDITOR_MODES.TRANSLATE)"
    >
      {{ $t('CONVERSATION.REPLYBOX.TRANSLATE') }}
    </button>
    <div
      class="absolute shadow-sm rounded-full h-6 w-[var(--chip-width)] ease-in-out translate-x-[var(--translate-x)] rtl:translate-x-[var(--rtl-translate-x)] bg-n-solid-1"
      :class="{
        'transition-all duration-300': !disabled && !isReplyRestricted,
      }"
      :style="{
        '--chip-width': width,
        '--translate-x': translateValue,
        '--rtl-translate-x': `calc(-1 * var(--translate-x))`,
      }"
    />
  </div>
</template>
